import type { Currency } from '@shared/utils';
import type { YourNumbersDrawnLine } from '../../../lottery-results/components/YourNumbersSection/YourNumbersPod/YourNumbersPod';
import { getMatchedNumbers, hasMatchedNumbers } from '../../../lottery-results/liveDraw/getMatchedNumbers';
import { toNumberList, type DrawnNumbers, type DrawnNumbersSource } from '../../../lottery-results/liveDraw/getRevealedNumbers';
import type { Line, LotteryName, Participation, ParticipationState, WinningInfo } from '../../types';

/**
 * States where the prize on a participation is final.
 *
 * Anything else means this copy of the ticket predates the draw, so an absent prize reads as "not
 * known" rather than "won nothing" — the distinction the win board depends on. `EVALUATED` is only
 * final when it carries `realWinning`; see {@link isParticipationSettled}.
 */
const SETTLED_PARTICIPATION_STATES: ReadonlySet<ParticipationState> = new Set(['BILLED', 'FINISHED', 'VOID']);

/**
 * Staging can reach `EVALUATED` before the prize exists: no `realWinning`, and the ticket's
 * `totalWinning` still 0 until `BILLED`. Settling there would show a win as a confirmed $0, and
 * then un-settle it once the participation moved on to `BILLED`.
 */
function isParticipationSettled(participation: Participation | undefined): participation is Participation {
  if (participation?.participationState === 'EVALUATED') {
    return participation.realWinning !== undefined;
  }

  return participation !== undefined && SETTLED_PARTICIPATION_STATES.has(participation.participationState);
}

function getLinePrizeCents(info: WinningInfo, currency: Currency): number | undefined {
  const total = (info.winningPerLine?.[currency] ?? []).reduce((sum, value) => sum + value, 0);
  return total > 0 ? total : undefined;
}

/**
 * The drawing's balls, main and extra apart. The participation's `result` only carries the main
 * numbers, so it is the fallback for a caller that did not pass the drawing.
 */
function resolveDrawn(participation: Participation | undefined, lotteryName: LotteryName, drawnNumbers: DrawnNumbersSource | undefined): DrawnNumbers {
  if (drawnNumbers) {
    return { numbers: toNumberList(drawnNumbers.numbers), ...(drawnNumbers.extraNumbers ? { extraNumbers: toNumberList(drawnNumbers.extraNumbers) } : {}) };
  }

  return { numbers: toNumberList(participation?.result?.[lotteryName]) };
}

/**
 * Per-line prizes and matched balls for Your Numbers.
 *
 * A settled line with a prize is included even when it matched nothing, so the $1 Daily Million
 * return still shows. Matched numbers are what paints those balls on a previous draw, extras
 * included, by the same rule the live reveal uses.
 */
function toDrawnLines(
  participation: Participation | undefined,
  lotteryName: LotteryName,
  currency: Currency,
  lines: Line[] = [],
  drawnNumbers?: DrawnNumbersSource
): Record<number, YourNumbersDrawnLine> | undefined {
  const winningInfo = participation?.lineWinningInfo[lotteryName];
  if (!winningInfo?.length) {
    return undefined;
  }

  const drawn = resolveDrawn(participation, lotteryName, drawnNumbers);
  const linesByNumber = new Map(lines.map(line => [line.lineNumber, line]));
  const drawnLines: Record<number, YourNumbersDrawnLine> = {};

  winningInfo.forEach(info => {
    const prize = getLinePrizeCents(info, currency);
    const line = linesByNumber.get(info.lineNumber);
    const matched = line ? getMatchedNumbers(line, drawn) : { numbers: [] };
    const hasMatch = hasMatchedNumbers(matched);
    if (prize === undefined && !hasMatch) {
      return;
    }

    drawnLines[info.lineNumber] = {
      ...(prize !== undefined ? { prize } : {}),
      ...(hasMatch ? { matching: { numbers: matched.numbers, ...(line?.extraNumbers ? { extraNumbers: matched.extraNumbers ?? [] } : {}), isHighlighted: (prize ?? 0) > 0 } } : {}),
    };
  });

  return Object.keys(drawnLines).length > 0 ? drawnLines : undefined;
}

export { getLinePrizeCents, isParticipationSettled, toDrawnLines };
